import React, { useEffect, useState, useReducer } from 'react'
import axios from '~/data/http/axios'

import { useDispatch, useSelector } from 'react-redux'
import { useHasWriteAccess } from '~/Common-Objects/hooks/permissions'
import { useIsTranspond } from '~/Common-Objects/hooks/siteMaster'
import { alterSiteDrawer, alterSnackbar, handleGenericError } from '~/data/actions/siteActions'

import { SENDING_DOMAIN, TRACKING_DOMAIN, CDN_DOMAIN } from '~/Classes/domains'
import postHogFunctions from '~/Classes/posthog'

import i18n from '~/i18n'

import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Column from '~/Common-Objects/Grids/Column/Column'
import DataTable1 from '~/Common-Objects/Tables/DataTable1/DataTable1'
import DomainManagement from '~/Common-Objects/DomainManagement'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import OptionsDropdown from '~/Common-Objects/UI/OptionsDropdown/OptionsDropdown'
import PagingBlock from '~/Common-Objects/Tables/DataTablePaging/DataTablePaging'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import Row from '~/Common-Objects/Grids/Row/Row'
import SmallLineGraph from '~/Common-Objects/UI/Graphs/SmallLineGraph/SmallLineGraph'
import SquareChip from '~/Common-Objects/UI/Chips/SquareChip.jsx'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import BuyDomainStep1 from './components/buyDomainStep1'
import BuyDomainStep2 from './components/buyDomainStep2'
import DeleteDomain from './components/deleteDomain'
import MxRecords from './components/mxRecords'
import AddDomainAdrom from './components/addDomainAdrom'
import AuthenticateDomainAdrom from './components/authenticateDomainAdrom'
import { Tooltip } from 'react-tooltip'
import DeleteTrackingDomain from './components/deleteTrackingDomain'
import DeleteCdnDomain from './components/deleteCdnDomain'
import ChangePreviewEmailAddress from './components/changePreviewEmail'

export default function Domains() {
    const siteMaster = useSelector((state) => state.siteMaster)
    const hasWriteAccess = useHasWriteAccess('domains')
    const isTranspond = useIsTranspond()
    const dispatch = useDispatch()

    const [loading, setLoading] = useState(true)
    const [isAdrom, setIsAdrom] = useState(siteMaster.siteId === 2)
    const [alertMessage, setAlertMessage] = useState('')
    const [alertOpen, setAlertOpen] = useState(false)

    const [searchTerm, setSearchTerm] = useState('')
    const pageSize = 10

    const [totalDomains, setTotalDomains] = useState(0)
    const [currentPage, setCurrentPage] = useState(0)

    const [tableData, setTableData] = useState([])

    const [addingDomain, setAddingDomain] = useState(false)
    const [addingMxRecords, setAddingMxRecords] = useState(false)
    const [addingDomainStep, setAddingDomainStep] = useState('addDomain')
    const [selectedDomain, setSelectedDomain] = useState(null)

    useEffect(() => {
        loadDomains()
    }, [])

    useEffect(() => {
        loadDomains()
    }, [searchTerm, currentPage])

    async function setDefaultPreviewDomain(domain) {
        try {
            await axios.put(`/accountMaster/domain/${domain.id}/setPreviewDefault`)
            loadDomains()
        } catch (error) {
            dispatch(handleGenericError(error))
            throw error
        }
    }

    async function loadDomains() {
        try {
            let query = ''
            if (searchTerm.length >= 3) {
                query = `&searchText=${searchTerm}`
            }

            const domains = await axios.get(`/accountMaster/domain?page=${currentPage}&limit=${pageSize}&${query}`)
            setTotalDomains(domains.data.count)

            let tableDataTmp = domains.data.domains.map((domain) => {
                let verified = (
                    <div
                        className='text-green'
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', alignItems: 'center' }}
                    >
                        <Icons
                            style={{ display: 'flex', height: 16, width: 16 }}
                            icon='tickCircle'
                            color={'#4AB7A9'}
                        />
                        <div style={{ fontSize: 13, fontWeight: '500' }}>Verified</div>
                    </div>
                )
                if (!domain.verified)
                    verified = (
                        <div
                            className='text-red'
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', alignItems: 'center' }}
                        >
                            <Icons style={{ display: 'flex' }} icon='alert' color={'#E75C5A'} />
                            <div style={{ fontSize: 13, fontWeight: '500' }}>Not Verified</div>
                        </div>
                    )

                let authenticated = (
                    <div
                        className='text-green'
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', alignItems: 'center' }}
                    >
                        <Icons
                            style={{ display: 'flex', height: 16, width: 16 }}
                            icon='tickCircle'
                            color={'#4AB7A9'}
                        />
                        <div style={{ fontSize: 13, fontWeight: '500' }}>Authenticated</div>
                    </div>
                )
                if (!domain.authenticated)
                    authenticated = (
                        <div
                            className='text-red'
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', alignItems: 'center' }}
                        >
                            <Icons style={{ display: 'flex' }} icon='alert' color={'#E75C5A'} />
                            <div style={{ fontSize: 13, fontWeight: '500' }}>Not Authenticated</div>
                        </div>
                    )

                let deliveryScore = 0
                if (domain.verified) deliveryScore += 1
                if (domain.authenticated) deliveryScore += 5
                if (domain.dmarc) deliveryScore += 2
                if (domain.cdnDomain?.authenticated) deliveryScore += 1
                if (domain.trackingDomain?.authenticated) deliveryScore += 1

                let cta = ''
                if (!domain.verified && !isAdrom) {
                    cta = (
                        <Button
                            className='button-primary button-small mar-r5'
                            onClick={() => {
                                setAddingDomain(true)
                                setSelectedDomain(domain.id)
                                setAddingDomainStep('addDomain')
                            }}
                            iconRight={<Icons icon='arrowRight' />}
                            postHogTag='my_domains_button_verify_domain'
                        >
                            Verify Domain
                        </Button>
                    )
                } else if (
                    !domain.authenticated ||
                    !domain.dmarc ||
                    !domain.cdnDomain?.authenticated ||
                    !domain.trackingDomain?.authenticated
                ) {
                    if (isAdrom) {
                        cta = (
                            <Button
                                className='button-primary button-small mar-r5'
                                onClick={() => {
                                    dispatch(
                                        alterSiteDrawer(
                                            true,
                                            true,
                                            'right',
                                            <AuthenticateDomainAdrom
                                                domain={domain}
                                                onAuthenticated={() => {
                                                    loadDomains()
                                                    dispatch(alterSiteDrawer(false))
                                                }}
                                            />,
                                            true,
                                            '700px'
                                        )
                                    )
                                }}
                                iconRight={<Icons icon='arrowRight' />}
                                postHogTag='my_domains_button_authenticate'
                            >
                                Authenticate
                            </Button>
                        )
                    } else {
                        let improvingDeliverability = false
                        if (
                            domain.authenticated &&
                            (!domain.dmarc || !domain.cdnDomain?.authenticated || !domain.trackingDomain?.authenticated)
                        ) {
                            improvingDeliverability = true
                        }

                        cta = (
                            <Button
                                className={`button-small mar-r5${!improvingDeliverability ? ' button-primary' : ''}`}
                                onClick={() => {
                                    if (!improvingDeliverability) {
                                        postHogFunctions.capture('my_domains_button_authenticate')
                                    }
                                    setAddingDomain(true)
                                    setSelectedDomain(domain.id)
                                    setAddingDomainStep('dnsInstructions')
                                }}
                                {...(!improvingDeliverability ? { iconRight: <Icons icon='arrowRight' /> } : {})}
                                postHogTag={
                                    improvingDeliverability
                                        ? 'my_domains_button_improve'
                                        : 'my_domains_button_authenticate'
                                }
                            >
                                {improvingDeliverability ? 'Improve Deliverability' : 'Authenticate Domain'}
                            </Button>
                        )
                    }
                }
                return {
                    id: {
                        value: domain.id
                    },
                    favIcon: {
                        headerValue: ' ',
                        value:
                            domain.favIcon && domain.favIcon.length > 1 ? (
                                <img
                                    src={domain.favIcon}
                                    alt='Favicon'
                                    style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '10px',
                                        boxShadow: 'inset 0px 0px 1px rgba(0,0,0,0.32)'
                                    }}
                                />
                            ) : (
                                <Icons icon='genericDomain' style={{ width: 32, height: 32 }} />
                            ),
                        width: 32,
                        align: 'center'
                    },
                    domainName: {
                        headerValue: 'Domain',
                        value: (
                            <>
                                <span style={{ fontWeight: 600 }}>{domain.domainName}</span>
                                {domain.defaultPreviewDomain && (
                                    <SquareChip className='mar-l10'>Test Domain</SquareChip>
                                )}
                            </>
                        )
                    },
                    verification: {
                        headerValue: (
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                Verification
                                <span
                                    data-tooltip-content={
                                        'Confirms that you own, control, or have access to this domain.'
                                    }
                                    data-tooltip-id={'verification-tip'}
                                >
                                    <Icons
                                        icon='questionCircle'
                                        className='mar-l5'
                                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                                    />
                                </span>
                                <Tooltip id='verification-tip' />
                            </span>
                        ),
                        value: verified
                    },
                    authentication: {
                        headerValue: (
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                Authentication
                                <span
                                    data-tooltip-content={'Improve trust and deliverability by adding DNS records.'}
                                    data-tooltip-id={'authentication-tip'}
                                >
                                    <Icons
                                        icon='questionCircle'
                                        className='mar-l5'
                                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                                    />
                                </span>
                                <Tooltip id='authentication-tip' />
                            </span>
                        ),
                        value: authenticated
                    },
                    deliverabilityScore: {
                        headerValue: (
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                Deliverability Score
                                <span
                                    data-tooltip-content={
                                        "A rating based on your domain's authentication. A higher scores mean better inbox deliverability."
                                    }
                                    data-tooltip-id={'deliverability-tip'}
                                >
                                    <Icons
                                        icon='questionCircle'
                                        className='mar-l5'
                                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                                    />
                                </span>
                                <Tooltip id='deliverability-tip' />
                            </span>
                        ),
                        value: (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <div style={{ fontSize: 13 }}>{deliveryScore}/10</div>
                                <SmallLineGraph
                                    hideText={true}
                                    color={'#4AB7A9'}
                                    width='100%'
                                    height='5'
                                    val1={deliveryScore}
                                    val2={10}
                                    val1Label={undefined}
                                />
                            </div>
                        ),
                        width: 200
                    },
                    cta: {
                        headerValue: '',
                        value: cta,
                        cellStyle: { textAlign: 'right' }
                    },
                    optionsCol: {
                        headerValue: ' ',
                        value: (
                            <OptionsDropdown popWidth='150px'>
                                <ul>
                                    {!domain.defaultPreviewDomain && (
                                        <li>
                                            <a
                                                onClick={() => {
                                                    setDefaultPreviewDomain(domain)
                                                }}
                                            >
                                                Set as Test Domain
                                            </a>
                                        </li>
                                    )}
                                    {domain.verified && (
                                        <li>
                                            <a
                                                onClick={() => {
                                                    changePreviewEmailAddress(domain)
                                                }}
                                            >
                                                Change Preview Email
                                            </a>
                                        </li>
                                    )}

                                    {!domain.mxRecordAdded && domain.verified && domain.authenticated && (
                                        <>
                                            <li>
                                                <a
                                                    onClick={() => {
                                                        setupMxRecords(domain)
                                                    }}
                                                >
                                                    Setup MX Records
                                                </a>
                                            </li>
                                            <hr className='mar-b0' />
                                        </>
                                    )}

                                    {domain.trackingDomain && (
                                        <li>
                                            <a
                                                onClick={() => {
                                                    deleteTrackingDomain(domain)
                                                }}
                                                className='text-red'
                                            >
                                                Delete Tracking Domain
                                            </a>
                                        </li>
                                    )}
                                    {domain.cdnDomain && (
                                        <li>
                                            <a
                                                onClick={() => {
                                                    deleteCdnDomain(domain)
                                                }}
                                                className='text-red'
                                            >
                                                Delete CDN Domain
                                            </a>
                                        </li>
                                    )}
                                    <li>
                                        <a
                                            onClick={() => {
                                                deleteDomain(domain)
                                            }}
                                            className='text-red'
                                        >
                                            Delete Domain
                                        </a>
                                    </li>
                                </ul>
                            </OptionsDropdown>
                        ),
                        orderBy: false,
                        fixed: true,
                        width: 20
                    }
                }
            })

            setTableData(tableDataTmp)

            setLoading(false)
        } catch (error) {
            dispatch(handleGenericError(error))
            setLoading(false)
            throw error
        }
    }

    const handleSearch = (e) => {
        setCurrentPage(0)
        setSearchTerm(e.target.value)
    }

    const changePreviewEmailAddress = (domain) => {
        dispatch(
            alterSiteDrawer(
                true,
                true,
                'right',
                <ChangePreviewEmailAddress
                    id={domain.id}
                    onChanged={() => {
                        loadDomains()
                        dispatch(alterSiteDrawer(false))
                    }}
                />,
                true,
                '700px'
            )
        )
    }

    const buyDomain = () => {
        dispatch(
            alterSiteDrawer(true, true, 'right', <BuyDomainStep1 onContinue={handleBuyDomainContinue} />, true, '700px')
        )
    }
    function handleBuyDomainContinue(domainName, domainResult) {
        dispatch(
            alterSiteDrawer(
                true,
                true,
                'right',
                <BuyDomainStep2
                    domainName={domainName}
                    domainResult={domainResult}
                    onBought={() => {
                        loadDomains()
                        dispatch(alterSiteDrawer(false))
                    }}
                    onCloseDrawer={() => {
                        dispatch(alterSiteDrawer(false))
                    }}
                />,
                true,
                '700px'
            )
        )
    }

    const setupMxRecords = (domain) => {
        setAddingMxRecords(true)
        setSelectedDomain(domain)
    }

    const deleteDomain = (domain) => {
        dispatch(
            alterSiteDrawer(
                true,
                true,
                'right',
                <DeleteDomain
                    domain={domain}
                    onDelete={handleDeleteDomain}
                    onDeleted={() => {
                        loadDomains()
                        dispatch(alterSiteDrawer(false))
                    }}
                />,
                true,
                '460px'
            )
        )
    }

    const deleteTrackingDomain = (domain) => {
        dispatch(
            alterSiteDrawer(
                true,
                true,
                'right',
                <DeleteTrackingDomain
                    domain={domain}
                    onDelete={handleDeleteTrackingDomain}
                    onDeleted={() => {
                        loadDomains()
                        dispatch(alterSiteDrawer(false))
                    }}
                />,
                true,
                '460px'
            )
        )
    }

    const deleteCdnDomain = (domain) => {
        dispatch(
            alterSiteDrawer(
                true,
                true,
                'right',
                <DeleteCdnDomain
                    domain={domain}
                    onDelete={handleDeleteCdnDomain}
                    onDeleted={() => {
                        loadDomains()
                        dispatch(alterSiteDrawer(false))
                    }}
                />,
                true,
                '460px'
            )
        )
    }

    const handleDeleteDomain = async (domain) => {
        try {
            await axios.delete(`/accountMaster/domain/${domain.id}`)
        } catch (error) {
            dispatch(handleGenericError(error))
            throw error
        }
    }

    const handleDeleteTrackingDomain = async (domain) => {
        try {
            await axios.delete(`/accountMaster/domain/tracking/${domain.id}`)
            dispatch(alterSnackbar(true, 'Tracking domain deleted successfully', 5000))
        } catch (error) {
            dispatch(handleGenericError(error))
            throw error
        }
    }

    const handleDeleteCdnDomain = async (domain) => {
        try {
            await axios.delete(`/accountMaster/domain/cdn/${domain.id}`)
            dispatch(alterSnackbar(true, 'CDN domain deleted successfully', 5000))
        } catch (error) {
            dispatch(handleGenericError(error))
            throw error
        }
    }

    if (loading) return <LoadingIcons />

    return (
        <div className='company-domains'>
            {addingDomain && (
                <DomainManagement
                    finish={() => {
                        setAddingDomain(false)
                        setSelectedDomain(null)
                        setAddingDomainStep('addDomain')
                        loadDomains()
                    }}
                    forDomainId={selectedDomain}
                    step={addingDomainStep}
                />
            )}

            {addingMxRecords && (
                <MxRecords
                    domain={selectedDomain}
                    finish={() => {
                        setAddingMxRecords(false)
                        setSelectedDomain(null)
                        loadDomains()
                    }}
                />
            )}

            {(tableData.length > 0 || searchTerm.length > 0) && (
                <Row className='mar-b16'>
                    <Column xs={12} sm={6}>
                        <TextInput
                            value={searchTerm}
                            onChange={handleSearch}
                            placeholder={i18n.t('company:domains.searchPlaceholder')}
                            iconLeft={<Icons icon='magnifier' color='#37393C' />}
                        />
                    </Column>
                    <Column xs={12} sm={6}>
                        {hasWriteAccess && (
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '12px',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end'
                                }}
                            >
                                {isTranspond && (
                                    <Button onClick={buyDomain} iconLeft={<Icons icon='document' />}>
                                        {i18n.t('company:domains.buyDomain')}
                                    </Button>
                                )}

                                <Button
                                    iconLeft={<Icons icon='plus' />}
                                    onClick={() => {
                                        if (isAdrom) {
                                            dispatch(
                                                alterSiteDrawer(
                                                    true,
                                                    true,
                                                    'right',
                                                    <AddDomainAdrom
                                                        onAdded={() => {
                                                            loadDomains()
                                                            dispatch(alterSiteDrawer(false))
                                                        }}
                                                    />,
                                                    true,
                                                    '700px'
                                                )
                                            )
                                        } else {
                                            postHogFunctions.capture('my_domains_button_add_domain')
                                            setAddingDomain(true)
                                            setSelectedDomain(null)
                                            setAddingDomainStep('addDomain')
                                        }
                                    }}
                                    postHogTag='my_domains_button_add_domain'
                                >
                                    {i18n.t('company:domains.sendingAdd')}
                                </Button>
                            </div>
                        )}
                    </Column>
                </Row>
            )}

            <DataTable1
                noResultsTxt={
                    <Paper className='paper-dotted'>
                        {searchTerm.length > 0 && (
                            <>
                                <h4 className='mar-b8'>No Domains Matched Your Search</h4>
                                <p className='text-grey'>Please try a different search term.</p>
                            </>
                        )}
                        {searchTerm.length === 0 && (
                            <>
                                <h4 className='mar-b8'>No Domains Available</h4>
                                <p className='text-grey'>
                                    To add and verify an existing domain you own, press the{' '}
                                    <strong className='text-black'>Add Domain</strong> button below.
                                </p>

                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '12px',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Button
                                        className='button-primary'
                                        onClick={() => {
                                            postHogFunctions.capture('my_domains_button_add_domain')
                                            setAddingDomain(true)
                                            setSelectedDomain(null)
                                            setAddingDomainStep('addDomain')
                                        }}
                                        iconLeft={<Icons icon='plus' />}
                                        postHogTag='my_domains_button_add_domain'
                                    >
                                        Add Domain
                                    </Button>

                                    <Button
                                        className=''
                                        onClick={() => {}}
                                        iconLeft={<Icons icon='document' />}
                                    >
                                        Buy Domain
                                    </Button>
                                </div>
                            </>
                        )}
                    </Paper>
                }
                tableData={tableData}
                loadingData={false}
                dataUpdater={() => {}}
                width='100%'
                pageSize='100'
                hasCheckBoxes='no'
                sortedColumn={''}
                sortedDirection={''}
            />

            <PagingBlock
                totalRows={totalDomains}
                pageSize={pageSize}
                numberOfLinks='10'
                currentPage={currentPage}
                changePage={(newPage) => {
                    setCurrentPage(newPage)
                }}
                text={i18n.t('page') + ':'}
            />
        </div>
    )
}
